import { useEffect, useRef, useState } from 'react';

/**
 * The value as it should be shown, held back while a mouse button is down.
 *
 * A message above the workspace pushes everything below it down. A refused save
 * raises one the moment focus leaves a field, which is usually the press of a
 * click somewhere else, so the target moved between press and release and the
 * click missed. While a button is held the previous value stays on screen; the
 * new one appears once the click (or drag) has finished. Keyboard changes show
 * immediately.
 */
export function useAfterPointerRelease<T>(value: T): T {
  const [shown, setShown] = useState(value);
  const latest = useRef(value);
  latest.current = value;
  const held = useRef(false);
  useEffect(() => {
    let release = 0;
    const down = () => {
      held.current = true;
      window.clearTimeout(release);
    };
    // After the whole press/release/click sequence has been dispatched.
    const up = () => {
      held.current = false;
      window.clearTimeout(release);
      release = window.setTimeout(() => setShown(latest.current), 0);
    };
    window.addEventListener('pointerdown', down, true);
    window.addEventListener('pointerup', up, true);
    window.addEventListener('pointercancel', up, true);
    window.addEventListener('blur', up);
    return () => {
      window.clearTimeout(release);
      window.removeEventListener('pointerdown', down, true);
      window.removeEventListener('pointerup', up, true);
      window.removeEventListener('pointercancel', up, true);
      window.removeEventListener('blur', up);
    };
  }, []);
  useEffect(() => {
    if (!held.current) setShown(value);
  }, [value]);
  return held.current ? shown : value;
}
